import BugReportIcon from '@mui/icons-material/BugReport'
import CodeIcon from '@mui/icons-material/Code'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import SchoolIcon from '@mui/icons-material/School'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { AppBar, Avatar, Box, CircularProgress, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Tooltip, Typography } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React, { forwardRef, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import useBar from './Bar'
import CButton from './CButton'
import NavigationItems from './NavigationItems'
import { fullScreenAtom, isAppContentScrolledDownAtom, viewTypeAtom } from '../../contexts/AtomUtil'
import { serverVersionLoadableAtom } from '../../contexts/AtomUtil'
import { hasSeenTooltipAtom } from '../../contexts/AtomUtil'
import Config from '../../data/Config'
import { isAuthenticatedAtom } from '../../domains/auth/AtomAuth'
import { logoutCommandAtom } from '../../domains/auth/AuthCommand'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import { isInstructor } from '../../share/utility'
import ColorModeIconDropdown from '../../theme/ColorModeIconDropdown'
import { avatarUrl } from '../../utils/gravatar'
import { isBookingPage, isHistoryPage } from '../../utils/utility'
import BugReportDialog from '../dialog/BugReportDialog'

const ViewSwitchingButton = forwardRef(({ viewType, handleViewChange, ...props }, ref) => {
  return (
    <CButton onClick={handleViewChange} ref={ref} {...props}>
      {viewType === 'instructor' ? <VisibilityIcon fontSize="small" /> : <SchoolIcon fontSize="small" />}
    </CButton>
  )
})

export default function TopNavigation() {
  // State for controlling the side drawer's open/close status
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const fullScreen = useAtomValue(fullScreenAtom)

  // 添加滚动状态
  const [isScrolled, setIsScrolled] = useAtom(isAppContentScrolledDownAtom)

  // 检查 URL 参数中是否有 focus=true
  const searchParams = new URLSearchParams(location.search)
  const shouldHideNavigation = searchParams.get('focus') === 'true'

  // Get authentication related functions and user data

  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const logoutCommand = useSetAtom(logoutCommandAtom)
  const user = useAtomValue(currentUserAtom)

  // Get theme related functions
  const [viewType, setViewType] = useAtom(viewTypeAtom)
  const bar = useBar()

  const version = Config.APP_VERSION
  const serverVersionLoadable = useAtomValue(serverVersionLoadableAtom)
  const serverVersion = serverVersionLoadable.state === 'hasData' ? serverVersionLoadable.data : ''

  const [openInstructorTooltip, setOpenInstructorTooltip] = useState(false)
  const [hasSeenTooltip, setHasSeenTooltip] = useAtom(hasSeenTooltipAtom)

  // 添加对话框状态
  const [bugReportOpen, setBugReportOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  // 检查用户是否是第一次看到教练视图切换按钮
  useEffect(() => {
    if (isInstructor(user) && (isBookingPage(location) || isHistoryPage(location))) {
      if (!hasSeenTooltip) {
        setOpenInstructorTooltip(true)
        const timer = setTimeout(() => {
          handleTooltipClose()
        }, 10000)
        return () => clearTimeout(timer)
      }
    }
  }, [isBookingPage, isHistoryPage, location, user, hasSeenTooltip])

  // 关闭提示并存储已查看状态
  const handleTooltipClose = () => {
    setOpenInstructorTooltip(false)
    setHasSeenTooltip(true)
  }

  // Handle user logout
  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await logoutCommand()
      setDrawerOpen(false) // Close drawer after successful logout
      bar.success('Successfully logged out')
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
      bar.error('Logout failed')
      setIsLoggingOut(false)
    }
  }

  // Handle user login
  const handleLogin = () => {
    navigate('/login')
    setDrawerOpen(false)
  }

  const handleViewChange = () => {
    const newViewType = viewType === 'instructor' ? 'user' : 'instructor'
    setViewType(newViewType)
    bar.success(`Switched to ${newViewType === 'instructor' ? 'Instructor' : 'User'} View`)
  }

  // 处理报告错误按钮点击
  const handleReportBug = () => {
    // 关闭侧边抽屉
    setDrawerOpen(false)

    // 打开错误报告对话框
    setBugReportOpen(true)
  }

  // 关闭错误报告对话框
  const handleCloseBugReport = () => {
    setBugReportOpen(false)
  }

  // 如果 URL 参数中有 focus=true，则不渲染导航栏
  if (shouldHideNavigation) {
    return null
  }

  if (fullScreen) {
    return null
  }

  return (
    // A out box is required to prevent the content to take actual spaces.
    <>
      {/* Top App Bar */}
      {/* <AppBar color="default" sex={{}}> */}
      <AppBar color="default" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100 }}>
        {fullScreen ? null : (
          <Toolbar
            onClick={() => {
              setIsScrolled(false)
            }}
            variant="dense" // Important to set variant to dense to avoid extra height.
            sx={{
              mx: { xs: 0, sm: 2 },
              p: isScrolled ? 0 : 1.5,
              minHeight: 'auto',
              transition: 'all 0.3s ease-in-out',
            }}
          >
            {/* 左侧菜单按钮 */}
            <CButton title="Menu" onClick={() => setDrawerOpen(true)}>
              <MenuIcon fontSize={isScrolled ? 'small' : 'medium'} />
            </CButton>

            {/* 中间标题 */}
            <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: 'auto' }}>
              <Box
                component="a"
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.3s ease-in-out',
                  cursor: 'pointer',
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <img
                  src="/web-app-manifest-192x192.png"
                  alt="logo"
                  style={{
                    width: isScrolled ? 32 : 40,
                    height: isScrolled ? 32 : 40,
                    transition: 'all 0.3s ease-in-out',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    fontSize: isScrolled ? '0.8rem' : '0.9rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  Booking On Snow
                </Typography>
              </Box>
            </Box>

            {/* 右侧按钮组 */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {(isBookingPage(location) || isHistoryPage(location)) && isInstructor(user) && openInstructorTooltip && !isScrolled && (
                <ClickAwayListener onClickAway={handleTooltipClose}>
                  <div>
                    <Tooltip
                      open={openInstructorTooltip}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={
                        <Box sx={{ p: 1, maxWidth: 250, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2, border: '2px solid', borderColor: 'primary.main' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.dark', fontSize: '1rem' }}>
                            View Switching Feature
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, color: 'text.primary', bgcolor: 'action.hover', p: 1, borderRadius: 0.5 }}>
                            As an instructor, you can switch between "Instructor View" and "Student View" to access different features and interfaces.
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 500, display: 'block', textAlign: 'center' }}>
                            Click this button to switch views
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="bottom-end"
                    >
                      <ViewSwitchingButton viewType={viewType} handleViewChange={handleViewChange} />
                    </Tooltip>
                  </div>
                </ClickAwayListener>
              )}
              {(isBookingPage(location) || isHistoryPage(location)) && isInstructor(user) && !openInstructorTooltip && !isScrolled && (
                <ViewSwitchingButton
                  title={viewType === 'instructor' ? 'Switch to User View' : 'Switch to Instructor View'}
                  viewType={viewType}
                  handleViewChange={handleViewChange}
                />
              )}

              {/* 主题切换按钮 */}
              {!isScrolled && <ColorModeIconDropdown fontSize="small" size="small" />}
            </Box>
          </Toolbar>
        )}
      </AppBar>

      {/* Side Navigation Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          {/* User Profile Section - Only shown when user is logged in */}
          {user && (
            <>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 40, height: 40 }} src={avatarUrl(user, 40)}>
                  {user.firstName?.[0]}
                </Avatar>

                <Box>
                  <Typography variant="subtitle1">{`${user.firstName} ${user.lastName}`}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Navigation Items */}
          <NavigationItems user={user} onNavigate={() => setDrawerOpen(false)} />

          {/* Login/Logout Section */}
          <Divider sx={{ my: 2 }} />
          <List>
            <ListItem disablePadding>
              {version && (
                <ListItemButton sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CodeIcon />
                  </ListItemIcon>
                  <ListItemText primary={`App Version: ${version}`} />
                </ListItemButton>
              )}
            </ListItem>

            <ListItem disablePadding>
              {serverVersion && (
                <ListItemButton sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CodeIcon />
                  </ListItemIcon>
                  <ListItemText primary={`Server Version: ${serverVersion}`} />
                </ListItemButton>
              )}
            </ListItem>

            {/* 报告错误按钮 */}
            <ListItem disablePadding>
              <ListItemButton onClick={handleReportBug} sx={{ borderRadius: 1, mb: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <BugReportIcon />
                </ListItemIcon>
                <ListItemText primary="Report Bug" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              {isAuthenticated ? (
                <>
                  <ListItemButton onClick={handleLogout} disabled={isLoggingOut} sx={{ borderRadius: 1, mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>{isLoggingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}</ListItemIcon>
                    <ListItemText primary={isLoggingOut ? 'Logging out...' : 'Logout'} />
                  </ListItemButton>
                </>
              ) : (
                // Show Login button if user is not authenticated
                <ListItemButton onClick={handleLogin} sx={{ borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LoginIcon />
                  </ListItemIcon>
                  <ListItemText primary="Login" />
                </ListItemButton>
              )}
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* 错误报告对话框 */}
      <BugReportDialog open={bugReportOpen} onClose={handleCloseBugReport} user={user} version={version} />
    </>
  )
}
